import { useCallback, useEffect, useRef, useState } from 'react';

import { isParticlePapEligible } from '../features/ttqib/papDisplay';
import { knowledgeService, type KnowledgeParticlePapView } from '../services/knowledgeService';

const POLL_INTERVAL_MS = 15_000;
const MAX_POLL_ATTEMPTS = 8;

export function useKnowledgeParticlePap(input: {
  particleId?: string | null;
  subject?: string | null;
  questions?: unknown;
  answers?: unknown;
  enabled?: boolean;
}) {
  const [papView, setPapView] = useState<KnowledgeParticlePapView | null>(null);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollAttemptsRef = useRef(0);
  const eligible = Boolean(input.enabled && input.particleId && isParticlePapEligible({
    subject: input.subject,
    questions: input.questions,
    answers: input.answers,
  }));

  const fetchPap = useCallback(async (kickoff = false) => {
    if (!eligible || !input.particleId) {
      setPapView(null);
      setLoading(false);
      setError(null);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const view = await knowledgeService.getParticlePap(input.particleId, { kickoff });
      setPapView(view);
      return view;
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : '加载隐性能力失败';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [eligible, input.particleId]);

  const rebuildPap = useCallback(async () => {
    if (!eligible || !input.particleId) {
      return null;
    }

    setRebuilding(true);
    setError(null);
    try {
      const view = await knowledgeService.extractParticlePap(input.particleId, { force: true });
      setPapView(view);
      if (view.extractionStatus === 'failed') {
        setError(view.failure?.message || '隐性能力重构失败');
      }
      return view;
    } catch (rebuildError) {
      const message = rebuildError instanceof Error ? rebuildError.message : '重构隐性能力失败';
      setError(message);
      return null;
    } finally {
      setRebuilding(false);
    }
  }, [eligible, input.particleId]);

  useEffect(() => {
    pollAttemptsRef.current = 0;
    if (!eligible) {
      setPapView(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async (kickoff = false) => {
      const view = await fetchPap(kickoff);
      if (cancelled || !view) return;
      const shouldContinue = view.extractionStatus === 'running'
        || view.extractionStatus === 'pending';
      if (shouldContinue && pollAttemptsRef.current < MAX_POLL_ATTEMPTS) {
        pollAttemptsRef.current += 1;
        timer = setTimeout(() => {
          void poll(false);
        }, POLL_INTERVAL_MS);
      }
    };

    void poll(true);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [eligible, fetchPap, input.particleId]);

  return {
    eligible,
    papView,
    loading: loading || rebuilding,
    rebuilding,
    error,
    refresh: fetchPap,
    rebuildPap,
  };
}
